import { useEffect, useId, useState } from 'react';
import { Alert, Box, Divider, TextField, Typography } from '@mui/material';
import { StyledDefaultPrimaryButton, StyledSimpleCard } from 'styles';
import SearchableTenantSelect from 'TeamModule/components/Filters/SearchableTenantSelect';
import SearchableRealmSelect from 'TeamModule/components/Filters/SearchableRealmSelect';
import { SandboxIsoStateIds } from 'SandboxModule/hooks/useSandboxes';
import { useNotification } from 'hooks/useNotification';
import { extractApiErrorMessage } from 'utils/extractApiErrorMessage';
import { TeamSearchItem } from 'TeamModule/api/searchTeamsByFieldsApi';
import { ScheduledMaintenanceWindow } from 'RealmModule/services/scheduledMaintenanceApi';
import {
  findTeamWindow,
  useClearScheduledMaintenance,
  useScheduledMaintenance,
  useSetScheduledMaintenance,
} from 'RealmModule/hooks/useScheduledMaintenance';
import SourceTeamSelect from './SourceTeamSelect';
import { formatEpochSeconds, toEpochSeconds, toLocalInputValue } from '../windowTime';

/**
 * Put a team into maintenance for a window of your choosing.
 *
 * Separate from starting a migration on purpose. The migration form schedules a window
 * that opens with the run, so its start can only ever be "now" — but a window is useful
 * on its own: announced ahead of time, held open across a manual finalize, or extended
 * when a run overruns. Here both ends are the operator's to pick, and a start in the
 * future is the normal case rather than a mistake.
 *
 * Scoped to the realm the team is CURRENTLY indexed in. Galaxy validates the team against
 * that realm, so for a team awaiting migration this is the source realm, and for one that
 * has already moved it is the target.
 */

/**
 * What the flag already says, and what it will refuse.
 *
 * Split out because it is a stack of independent conditions that pushed the card past the
 * complexity limit — and because none of them share state with the form above.
 */
const WindowStateAlerts = ({
  teamId,
  stateError,
  existingWindow,
  flagDisabled,
  unwritableRule,
}: {
  teamId: string;
  stateError: boolean;
  existingWindow?: ScheduledMaintenanceWindow;
  flagDisabled: boolean;
  unwritableRule?: ScheduledMaintenanceWindow;
}) => (
  <>
    {!!teamId && stateError && (
      <Alert severity="warning" sx={{ mt: 3 }}>
        Could not read the current windows, so whether this team already has one is unknown. Setting
        one will replace anything that is there.
      </Alert>
    )}

    {!!existingWindow && (
      <Alert severity="info" sx={{ mt: 3 }}>
        Team {teamId} already has a window:{' '}
        {existingWindow.startTime !== null && existingWindow.endTime !== null
          ? `${formatEpochSeconds(existingWindow.startTime)} → ${formatEpochSeconds(existingWindow.endTime)}`
          : (existingWindow.value ?? 'unreadable')}
        {/* isActive ignores GrowthBook's own schedule gate, so it is only an answer for a
            rule carrying none. */}
        {!existingWindow.hasScheduleRules && existingWindow.isActive ? ' (active now)' : ''}.
        Setting a new one replaces it.
        {existingWindow.hasScheduleRules &&
          ' It also carries GrowthBook schedule rules, which galaxy does not write and cannot evaluate, so whether it is serving right now can only be seen in GrowthBook.'}
      </Alert>
    )}

    {flagDisabled && (
      <Alert severity="error" sx={{ mt: 3 }}>
        The maintenance flag is disabled for this environment, so writes will be refused. Enable it
        in GrowthBook first.
      </Alert>
    )}

    {!!unwritableRule && (
      <Alert severity="error" sx={{ mt: 3 }}>
        A <code>{unwritableRule.type}</code> rule on this flag blocks writes: galaxy only writes{' '}
        <code>force</code> rules and refuses to touch a flag carrying any other kind.
        {(unwritableRule.ruleId || unwritableRule.description) && (
          <Box mt={1}>
            The rule is <code>{unwritableRule.ruleId ?? unwritableRule.description}</code>. It may
            belong to another team — it blocks this flag for everyone either way, and has to be
            removed in GrowthBook.
          </Box>
        )}
      </Alert>
    )}
  </>
);

export const MaintenanceWindowCard = () => {
  const helperId = useId();
  const { showSuccess, showError } = useNotification();

  const [tenant, setTenant] = useState('');
  const [realmId, setRealmId] = useState('');
  const [team, setTeam] = useState<TeamSearchItem | null>(null);
  const [start, setStart] = useState(() => toLocalInputValue(new Date()));
  const [end, setEnd] = useState(() =>
    toLocalInputValue(new Date(Date.now() + 4 * 60 * 60 * 1000)),
  );

  const teamId = team?.teamId ?? '';

  // A realm id belongs to one tenant, and the select renders blank for a value that has
  // left its options while still holding it — so without this the field looks empty and
  // the old id is what gets submitted.
  const changeTenant = (next: string) => {
    setTenant(next);
    setRealmId('');
  };

  // The team was searched inside one realm and means nothing outside it.
  useEffect(() => {
    setTeam(null);
  }, [tenant, realmId]);

  const {
    state,
    isLoading: stateLoading,
    isError: stateError,
    refetch,
  } = useScheduledMaintenance({ enabled: !!teamId });

  const existingWindow = findTeamWindow(state, { tenant, realmId, teamId });
  // Any non-force rule anywhere on the flag makes galaxy refuse to write it at all, even
  // one scoped to another team.
  const unwritableRule = state?.windows.find((w) => w.type !== 'force');

  const { setWindow, isSettingWindow } = useSetScheduledMaintenance();
  const { clearWindow, isClearingWindow } = useClearScheduledMaintenance();
  const isBusy = isSettingWindow || isClearingWindow;

  const startSeconds = toEpochSeconds(start);
  const endSeconds = toEpochSeconds(end);
  // Only the time-independent checks run here. "Is the end still in the future" depends on
  // the current time, so a value computed during render is stale the moment it paints —
  // that one runs at submit.
  const ordered = startSeconds !== null && endSeconds !== null && endSeconds > startSeconds;
  const canSubmit = !!tenant && !!realmId && !!teamId && ordered && !isBusy && !stateLoading;

  const onSet = async () => {
    if ((endSeconds as number) * 1000 <= Date.now()) {
      showError('The window has already ended. Set an end time in the future.');
      return;
    }

    try {
      await setWindow({
        tenant,
        realmId,
        teamIds: [teamId],
        startTime: startSeconds as number,
        endTime: endSeconds as number,
      });
      showSuccess(
        `Team ${teamId} is in maintenance from ${formatEpochSeconds(startSeconds as number)} to ${formatEpochSeconds(endSeconds as number)}.`,
      );
      refetch();
    } catch (error) {
      showError(`Could not set the window: ${extractApiErrorMessage(error as never)}`);
    }
  };

  const onClear = async () => {
    try {
      await clearWindow({ tenant, realmId, teamIds: [teamId] });
      showSuccess(`Maintenance window removed for team ${teamId}.`);
      refetch();
    } catch (error) {
      showError(`Could not clear the window: ${extractApiErrorMessage(error as never)}`);
    }
  };

  return (
    <StyledSimpleCard>
      <h3>Maintenance Window</h3>

      <Typography variant="caption" color="text.secondary">
        Sets the <code>scheduledMaintenanceFlag</code> rule for one team, so the product shows a
        maintenance state for the window you choose. Independent of starting a migration — the
        migration form schedules its own window that opens with the run.
      </Typography>

      <Box gap={12} display="flex" flexDirection="column" mt={3}>
        <SearchableTenantSelect
          value={tenant}
          onChange={changeTenant}
          label="Tenant"
          labelId={`${helperId}-tenant`}
          disabled={isBusy}
        />
        <SearchableRealmSelect
          tenantName={tenant}
          value={realmId}
          onChange={setRealmId}
          label="Realm"
          labelId={`${helperId}-realm`}
          disabled={isBusy}
          isoId={SandboxIsoStateIds.SandboxTable}
        />
        <SourceTeamSelect
          tenant={tenant}
          realmId={realmId}
          value={teamId}
          onChange={setTeam}
          scopeHint="Choose the tenant and realm the team is in first."
        />

        <Box display="flex" gap={2} flexWrap="wrap">
          <TextField
            label="Window starts at"
            type="datetime-local"
            value={start}
            onChange={(e) => setStart(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            disabled={isBusy}
            error={!!start && startSeconds === null}
            helperText={
              !!start && startSeconds === null
                ? 'Use the date picker — a date on its own is read as UTC midnight.'
                : 'May be in the future: the team enters maintenance then.'
            }
            sx={{ flex: 1, minWidth: 240 }}
          />
          <TextField
            label="Window ends at"
            type="datetime-local"
            value={end}
            onChange={(e) => setEnd(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            disabled={isBusy}
            error={(!!end && endSeconds === null) || (startSeconds !== null && !ordered)}
            helperText={
              !!end && endSeconds === null
                ? 'Use the date picker — a date on its own is read as UTC midnight.'
                : startSeconds !== null && !ordered
                  ? 'The end must be after the start.'
                  : 'Must be in the future.'
            }
            sx={{ flex: 1, minWidth: 240 }}
          />
        </Box>
      </Box>

      <WindowStateAlerts
        teamId={teamId}
        stateError={stateError}
        existingWindow={existingWindow}
        flagDisabled={!!state && !state.enabled}
        unwritableRule={unwritableRule}
      />

      <Divider sx={{ my: 6 }} />

      <Box display="flex" gap={2} flexWrap="wrap">
        <StyledDefaultPrimaryButton type="button" disabled={!canSubmit} onClick={onSet}>
          {isSettingWindow ? 'Setting…' : existingWindow ? 'Replace window' : 'Set window'}
        </StyledDefaultPrimaryButton>

        {!!existingWindow && (
          <StyledDefaultPrimaryButton type="button" disabled={isBusy} onClick={onClear}>
            {isClearingWindow ? 'Clearing…' : 'Clear window'}
          </StyledDefaultPrimaryButton>
        )}
      </Box>

      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
        Windows are not removed automatically. One stops applying at its end time, but the rule
        stays on the flag — and once a team moves to another realm galaxy can no longer address it
        here, so clear it before migrating if it should not follow the team.
      </Typography>
    </StyledSimpleCard>
  );
};

export default MaintenanceWindowCard;
